import { useTranslation } from "react-i18next";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import { formatDateTime } from "../../../../../utils/DateUtils";
import { formatPhoneNumber } from "../../../../../utils/FormatUtils";
import "./OrderDetail.css";

// Ícone por forma de pagamento, usado tanto no badge do cabeçalho quanto na
// lista de formas de pagamento da venda.
const PAYMENT_METHOD_ICON = {
    CASH: 'fa-money-bill-wave',
    CREDIT_CARD: 'fa-credit-card',
    DEBIT_CARD: 'fa-credit-card',
    PIX: 'fa-qrcode',
    COURTESY: 'fa-gift',
    SUBSCRIPTION: 'fa-repeat',
    MIXED: 'fa-layer-group',
    NOT_SELECTED: 'fa-circle-question',
};

const OrderDetail = ({ order }) => {
    const { t } = useTranslation();

    if (!order) return null;

    const curr = (val) => `${t('currency_symbol')} ${convertDoubleToStringPTBR(val)}`;

    return (
        <div className="order-detail">

            {/* Hero */}
            <div className="order-detail__hero">
                <div>
                    <p className="order-detail__number">
                        <i className="fa fa-receipt me-2" style={{ opacity: 0.85 }}></i>
                        {t('sale_number')} #{order.number}
                    </p>
                    <div className="order-detail__hero-badges">
                        <span className="order-detail__badge">
                            <i className={`fa ${PAYMENT_METHOD_ICON[order.paymentMethod] || 'fa-credit-card'}`}></i>
                            {t(order.paymentMethod.toLowerCase())}
                        </span>
                        {order.cancellationDate && (
                            <span className="order-detail__badge order-detail__badge--canceled">
                                <i className="fa fa-ban"></i> {t('canceled')}
                            </span>
                        )}
                    </div>
                </div>
                <div className="order-detail__hero-right">
                    <p className="order-detail__total">{curr(order.total)}</p>
                    <span className="order-detail__date">
                        <i className="fa fa-clock me-1"></i>{formatDateTime(order.requestDate)}
                    </span>
                </div>
            </div>

            {/* Info geral + Cliente */}
            <div className="order-detail__grid">
                <div className="order-detail__section">
                    <div className="order-detail__section-header">
                        <span className="order-detail__section-icon">
                            <i className="fa fa-info-circle"></i>
                        </span>
                        <h6 className="order-detail__section-title">{t('general_information')}</h6>
                    </div>
                    <div className="order-detail__items-grid">
                        {order.branch && (
                            <div className="order-detail__item">
                                <span className="order-detail__item-label">{t('branch')}</span>
                                <span className="order-detail__item-value">{order.branch.name}</span>
                            </div>
                        )}
                        {order.employee && (
                            <div className="order-detail__item">
                                <span className="order-detail__item-label">{t('employee')}</span>
                                <span className="order-detail__item-value">
                                    {order.employee.firstName} {order.employee.lastName}
                                </span>
                            </div>
                        )}
                        {order.cancellationDate && (
                            <div className="order-detail__item" style={{ gridColumn: '1 / -1', borderColor: '#fca5a5', background: '#fff1f2' }}>
                                <span className="order-detail__item-label" style={{ color: '#b42318' }}>{t('canceled')}</span>
                                <span className="order-detail__item-value" style={{ color: '#b42318' }}>
                                    {formatDateTime(order.cancellationDate)}
                                </span>
                            </div>
                        )}
                        {order.offDayDiscountOverride && (
                            <div className="order-detail__exception" role="status">
                                <span className="order-detail__exception-icon">
                                    <i className="fa fa-unlock-alt"></i>
                                </span>
                                <div>
                                    <span className="order-detail__exception-title">Exceção do benefício</span>
                                    <span className="order-detail__exception-text">
                                        Desconto do plano liberado fora dos dias previstos.
                                        {order.offDayDiscountOverrideBy && ` Autorizado por ${order.offDayDiscountOverrideBy}.`}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="order-detail__section">
                    <div className="order-detail__section-header">
                        <span className="order-detail__section-icon">
                            <i className="fa fa-user"></i>
                        </span>
                        <h6 className="order-detail__section-title">{t('customer_information')}</h6>
                    </div>
                    {order.customer ? (
                        <div className="order-detail__items-grid">
                            <div className="order-detail__item" style={{ gridColumn: '1 / -1' }}>
                                <span className="order-detail__item-label">{t('name')}</span>
                                <span className="order-detail__item-value">{order.customer.fullName}</span>
                            </div>
                            {order.customer.phone && (
                                <div className="order-detail__item">
                                    <span className="order-detail__item-label">{t('phone')}</span>
                                    <span className="order-detail__item-value">{formatPhoneNumber(order.customer.phone)}</span>
                                </div>
                            )}
                            {order.customer.email && (
                                <div className="order-detail__item">
                                    <span className="order-detail__item-label">{t('email')}</span>
                                    <span className="order-detail__item-value" style={{ wordBreak: 'break-all' }}>{order.customer.email}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="order-detail__empty">{t('no_customer_associated')}</p>
                    )}
                </div>
            </div>

            {/* Itens do pedido */}
            {order.items && order.items.length > 0 && (
                <div className="order-detail__section order-detail__section--full">
                    <div className="order-detail__section-header">
                        <span className="order-detail__section-icon">
                            <i className="fa fa-shopping-cart"></i>
                        </span>
                        <h6 className="order-detail__section-title">
                            {t('order_items')} <span style={{ color: '#667085', fontWeight: 400 }}>({order.items.length})</span>
                        </h6>
                    </div>
                    <div>
                        {order.items.map((item, idx) => (
                            <div key={idx} className="order-detail__item-row">
                                <span className="order-detail__item-name">{item.product.name}</span>
                                <span className="order-detail__item-qty">{item.qty}×</span>
                                {/* Valor antes do desconto da venda: as linhas somam o subtotal
                                    e o desconto aparece no resumo financeiro. */}
                                <span className="order-detail__item-price">{curr(item.totalBeforeManualDiscount ?? item.total)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Formas de pagamento (venda com pagamento dividido em mais de uma
                forma). Quando ausente, a venda usa o pagamento único legado,
                já mostrado no badge do cabeçalho e no resumo financeiro abaixo. */}
            {order.payments && order.payments.length > 0 && (
                <div className="order-detail__section order-detail__section--full">
                    <div className="order-detail__section-header">
                        <span className="order-detail__section-icon">
                            <i className="fa fa-money-bill-wave"></i>
                        </span>
                        <h6 className="order-detail__section-title">
                            {t('payment_methods')} <span style={{ color: '#667085', fontWeight: 400 }}>({order.payments.length})</span>
                        </h6>
                    </div>
                    <div>
                        {order.payments.map((payment, idx) => (
                            <div key={idx} className="order-detail__item-row">
                                <span className="order-detail__item-name">
                                    {t(payment.paymentMethod.toLowerCase())}
                                    {payment.receivedMoney != null && (
                                        <span className="order-detail__item-sub">
                                            {t('money_received')}: {curr(payment.receivedMoney)}
                                            {payment.moneyChange > 0 && ` · ${t('money_change')}: ${curr(payment.moneyChange)}`}
                                        </span>
                                    )}
                                    {payment.cardFlag && (
                                        <span className="order-detail__item-sub">{payment.cardFlag.name}</span>
                                    )}
                                </span>
                                <span className="order-detail__item-icon">
                                    <i className={`fa ${PAYMENT_METHOD_ICON[payment.paymentMethod] || 'fa-wallet'}`}></i>
                                </span>
                                <span className="order-detail__item-price">{curr(payment.value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Resumo financeiro */}
            <div className="order-detail__section order-detail__section--full">
                <div className="order-detail__section-header">
                    <span className="order-detail__section-icon">
                        <i className="fa fa-money-bill-wave"></i>
                    </span>
                    <h6 className="order-detail__section-title">{t('financial_summary')}</h6>
                </div>
                <div className="order-detail__summary">
                    <div className="order-detail__summary-row">
                        <span>{t('subtotal')}</span>
                        <span>{curr(order.subTotal)}</span>
                    </div>
                    {order.manualDiscountValue > 0 && (
                        <div className="order-detail__summary-row">
                            <span>
                                {t('discount')}
                                {/* O número digitado na venda (10% ou R$ 10,00) e quem deu o
                                    desconto, para a venda poder ser conferida depois. */}
                                {order.manualDiscountType === 'PERCENTAGE' && ` (${convertDoubleToStringPTBR(order.manualDiscountInput)}%)`}
                                {order.manualDiscountBy && (
                                    <span className="order-detail__item-sub">{order.manualDiscountBy}</span>
                                )}
                            </span>
                            <span>- {curr(order.manualDiscountValue)}</span>
                        </div>
                    )}
                    <div className="order-detail__summary-row order-detail__summary-row--total">
                        <span>{t('total')}</span>
                        <span>{curr(order.total)}</span>
                    </div>
                    {order.receivedMoney && (
                        <>
                            <div className="order-detail__summary-row">
                                <span>{t('received_money')}</span>
                                <span>{curr(order.receivedMoney)}</span>
                            </div>
                            {order.moneyChange > 0 && (
                                <div className="order-detail__summary-row order-detail__summary-row--success">
                                    <span>{t('change')}</span>
                                    <span>{curr(order.moneyChange)}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Observações */}
            {order.obs && (
                <div className="order-detail__section order-detail__section--full">
                    <div className="order-detail__section-header">
                        <span className="order-detail__section-icon">
                            <i className="fa fa-comment-alt"></i>
                        </span>
                        <h6 className="order-detail__section-title">{t('obs')}</h6>
                    </div>
                    <p className="order-detail__obs">{order.obs}</p>
                </div>
            )}
        </div>
    );
};

export default OrderDetail;
